import React, { useState, useEffect, useCallback } from 'react';
import {
  Play, Square, RefreshCw, Loader2, CheckCircle, AlertTriangle,
  Users, Briefcase, Building2, Eye, EyeOff, Copy, Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

export default function AdminDemoMode() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [showCredentials, setShowCredentials] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/demo/status`, { withCredentials: true });
      setStatus(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleAction = async (endpoint, label) => {
    setAction(label);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/demo/${endpoint}`, {}, { withCredentials: true });
      toast.success(res.data.message);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${label}`);
    } finally { setAction(null); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
      </div>
    );
  }

  const enabled = status?.enabled || false;

  return (
    <div data-testid="demo-mode-section">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="h-5 w-5 text-orange-500" />
        <h2 className="text-xl font-bold text-slate-900">Demo Mode</h2>
        <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${
          enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
        }`} data-testid="demo-status-badge">
          {enabled ? 'ACTIVE' : 'OFF'}
        </span>
      </div>

      <p className="text-sm text-slate-500 mb-5">
        Safe, isolated demo environment for recordings, investor demos, and onboarding.
        Demo data never mixes with real users, payments, or analytics.
      </p>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 mb-6">
        {!enabled ? (
          <button onClick={() => handleAction('enable', 'enable')} disabled={!!action}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
            data-testid="demo-enable-btn">
            {action === 'enable' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Enable Demo Mode
          </button>
        ) : (
          <button onClick={() => handleAction('disable', 'disable')} disabled={!!action}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 text-sm font-medium"
            data-testid="demo-disable-btn">
            {action === 'disable' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
            Disable Demo Mode
          </button>
        )}
        <button onClick={() => handleAction('reset', 'reset')} disabled={!!action || !enabled}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 text-sm font-medium"
          data-testid="demo-reset-btn">
          {action === 'reset' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Reset Demo Data
        </button>
      </div>

      {/* Status Info */}
      {enabled && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={<Users className="h-4 w-4" />} label="Demo Accounts" value={status?.demo_accounts || 0} />
            <StatCard icon={<Briefcase className="h-4 w-4" />} label="Tradespeople" value={11} />
            <StatCard icon={<Building2 className="h-4 w-4" />} label="Employers" value={1} />
            <StatCard icon={<CheckCircle className="h-4 w-4" />} label="Credits" value="500" />
          </div>

          {/* Demo Login Credentials */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Demo Employer Login</h3>
              <button onClick={() => setShowCredentials(!showCredentials)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                {showCredentials ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showCredentials ? 'Hide' : 'Show'}
              </button>
            </div>
            {showCredentials && (
              <div className="space-y-2">
                <CredentialRow label="Email" value={status?.demo_employer_email} onCopy={copyToClipboard} />
                <CredentialRow label="Password" value={status?.demo_password} onCopy={copyToClipboard} />
                <p className="text-xs text-slate-400 mt-2">
                  Login as this employer to demo search, unlock, chat, and hire flows.
                  500 credits pre-loaded. Stripe payments blocked for demo accounts.
                </p>
              </div>
            )}
          </div>

          {/* Trade Breakdown */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Demo Tradespeople</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <TradeRow trade="Painters" count={3} />
              <TradeRow trade="General Builders" count={2} />
              <TradeRow trade="Plasterers" count={2} />
              <TradeRow trade="Handymen" count={2} />
              <TradeRow trade="Roofers" count={2} />
            </div>
          </div>

          {/* Safety Info */}
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Isolation guarantees</p>
                <ul className="space-y-0.5 text-xs text-amber-700">
                  <li>Demo accounts excluded from real search when demo mode is off</li>
                  <li>No real emails sent to demo accounts</li>
                  <li>Stripe payments blocked for demo accounts</li>
                  <li>Demo data excluded from admin user counts and analytics</li>
                  <li>Reset restores all demo data to clean defaults</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
      <div className="flex items-center justify-center gap-1.5 text-slate-400 mb-1">{icon}<span className="text-xs">{label}</span></div>
      <p className="text-lg font-bold text-slate-900">{value}</p>
    </div>
  );
}

function CredentialRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center justify-between bg-white rounded border border-slate-200 px-3 py-2">
      <div>
        <span className="text-xs text-slate-500 mr-2">{label}:</span>
        <code className="text-sm font-mono text-slate-900">{value}</code>
      </div>
      <button onClick={() => onCopy(value)} className="text-slate-400 hover:text-slate-600"><Copy className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function TradeRow({ trade, count }) {
  return (
    <div className="flex items-center justify-between bg-white rounded border border-slate-200 px-3 py-1.5">
      <span className="text-slate-700">{trade}</span>
      <span className="text-xs font-bold text-slate-500">{count}</span>
    </div>
  );
}
